import React, { useEffect, useImperativeHandle, useRef, useState } from "react";

import type { ExtractRefType } from "@shapr3d/frontend-lib";
import classNames from "classnames";

import { Avatar } from "../../../../Comment/Avatar";
import InfoTooltip from "../../../../InfoTooltip/InfoTooltip";
import type { MenuPortal } from "./MenuPortal";
import { RoleSelector } from "./RoleSelector";
import type { ShareBadge } from "./ShareBadge";

import style from "./ShareRow.module.css";

type Entity<TRole extends string> = {
  role: TRole;
  name?: string;
  email?: string;
};

interface ShareRowProps<TRoles extends readonly [string, ...string[]]> {
  roles: TRoles;
  roleDisplayName: (role: TRoles[number]) => string;
  entity: Entity<TRoles[number]>;
  containerIntersectionObserver?: IntersectionObserver | null;
  isPending?: boolean;
  isError?: boolean;
  onUpdate?: (prevRole: TRoles[number], newRole: TRoles[number]) => Promise<unknown>;
  onRemove?: () => void;
  sharingBadges?: ShareBadge[];
  immutable?: boolean;
  isRequestAccess?: boolean;
  proRoles?: Set<TRoles[number]>;
  showPaywall?: () => void;
  isProRoleEligible?: boolean;
  tooltipText?: string;
  removeActionText?: string;
}

export type ShareRowRef = {
  closeContextMenu?: () => void;
};

const ShareRowRender = <TRoles extends readonly [string, ...string[]]>(
  {
    roles,
    roleDisplayName,
    entity,
    isPending,
    isError,
    onUpdate,
    onRemove,
    containerIntersectionObserver,
    sharingBadges,
    immutable = false,
    isRequestAccess = false,
    proRoles = new Set(),
    showPaywall,
    isProRoleEligible = true,
    removeActionText = "Remove...",
    tooltipText,
  }: ShareRowProps<TRoles>,
  ref: React.ForwardedRef<ShareRowRef>
) => {
  const formRef = useRef<HTMLFormElement>(null);
  const menuRef = useRef<ExtractRefType<typeof MenuPortal>>(null);

  useEffect(() => {
    if (formRef.current) {
      containerIntersectionObserver?.observe(formRef.current);
    }
    return () => containerIntersectionObserver?.disconnect();
  }, [containerIntersectionObserver]);

  useImperativeHandle(ref, () => ({
    closeContextMenu: () => {
      menuRef.current?.close?.();
    },
  }));

  const [currentRole, setCurrentRole] = useState(entity.role);
  useEffect(() => {
    setCurrentRole(entity.role);
  }, [entity.role]);

  return (
    <>
      <form ref={formRef} className={style.shareRow}>
        <Avatar
          disabled={sharingBadges?.includes("Invited") || isRequestAccess}
          title={entity.name ?? entity.email ?? "never"}
        />
        <div
          className={classNames(
            style.nameContainer,
            (sharingBadges?.includes("Invited") || isRequestAccess) && style.pending
          )}
        >
          <div>
            <div className={style.nameText}>{entity.name || entity.email}</div>
            {sharingBadges?.map((badge) => (
              <span className={style.badge} key={badge}>
                ({badge})
              </span>
            ))}
          </div>
          {entity.name && (
            <div title={entity.email} className={style.emailText}>
              {entity.email}
            </div>
          )}
        </div>
        <div className={style.roleContainer}>
          {tooltipText && <InfoTooltip content={tooltipText} />}
          {immutable && <div className={style.immutableRow}>{roleDisplayName(entity.role)}</div>}
          {!immutable && (
            <RoleSelector
              menuRef={menuRef}
              isPending={isPending}
              roleDisplayName={roleDisplayName}
              roles={roles}
              roleWatch={currentRole}
              onRemove={onRemove}
              isRequestAccess={isRequestAccess}
              removeActionText={removeActionText}
              onClick={async (role) => {
                if (isPending || role === currentRole) return;
                if (proRoles.has(role) && !isProRoleEligible) {
                  showPaywall?.();
                  return;
                }
                const prev = currentRole;
                setCurrentRole(role);
                try {
                  await onUpdate?.(prev, role);
                } catch {
                  setCurrentRole(prev);
                }
              }}
            />
          )}
        </div>
      </form>

      {isError && <span className={style.red}>{"Something went wrong. Please try again."}</span>}
    </>
  );
};

export const ShareRow = React.forwardRef(ShareRowRender) as <
  TRoles extends readonly [string, ...string[]],
>(
  props: ShareRowProps<TRoles> & React.RefAttributes<ShareRowRef>
) => React.ReactElement;
